import { useEffect, useRef, useState } from "react";
import { type AiConnection, api, type Me, type SignedOutElsewhere } from "./api.ts";
import { type Appearance, savedAppearance, setAppearance } from "./appearance.ts";
import { BillingSection } from "./Billing.tsx";
import { ConnectSteps } from "./Connect.tsx";
import { ConfirmDialog } from "./Dialog.tsx";
import { SendFeedback } from "./Feedback.tsx";
import { lastUsed } from "./format.ts";
import { GettingStarted } from "./GettingStarted.tsx";
import { HowItWorks } from "./HowItWorks.tsx";
import { useLoad } from "./hooks.ts";
import { MyCurrency } from "./MyCurrency.tsx";
import { usePersonEvents } from "./me-live.ts";
import { NotificationsSection } from "./Notifications.tsx";
import { ReportThis } from "./ReportThis.tsx";
import { navigate, stepState } from "./router.ts";
import { SettingsGroup } from "./SettingsGroup.tsx";
import { Avatar, Button, SegmentedControl, TextField } from "./souvenir.ts";
import { Prompt, Wordmark } from "./ui.tsx";
import { DeleteAccount, Legal, YourData } from "./YourData.tsx";

// Who I am in Tripmo, across all my trips: my photo and my name. A photo is cropped to a square and
// made small (256 px) here in the browser before it goes; Tripmo shows it to the people in my trips.

const SIDE = 256;

// The middle square of an image, small, as a JPEG.
async function square(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = SIDE;
  canvas.height = SIDE;
  const g = canvas.getContext("2d");
  if (!g) throw new Error("This browser can't prepare photos.");
  g.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, SIDE, SIDE);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't prepare that photo."))), "image/jpeg", 0.88),
  );
}

// What to ask your AI to learn the rest, for where you are (its `start` guide answers it).
const WHAT_CAN_I_DO = "What can I do with Tripmo?";

// How Tripmo works again, over the profile (a step of its own: Back, or its last button, closes it).
function HowAgain() {
  const close = () => {
    if (stepState().layer) history.back();
    else navigate("/profile", { replace: true });
  };
  return (
    <div className="tm-page tm-start-page">
      <header className="tm-bar">
        <Wordmark />
        <span className="tm-grow" />
        <Button variant="quiet" size="sm" onClick={close}>
          Your profile
        </Button>
      </header>
      <HowItWorks last="Done" onDone={close} />
    </div>
  );
}

// Connect your AI, over the profile, for someone who skipped it (a step of its own, as How Tripmo works
// is): getting started's own screen, from connecting on, and Connected as ever once an AI is allowed. Tripmo
// is asked about me again the moment it connects, so what follows has it.
function ConnectAgain({ me, onChanged }: { me: Me; onChanged: () => void }) {
  const trips = useLoad(api.trips, []);
  const close = () => {
    if (stepState().layer) history.back();
    else navigate("/profile", { replace: true });
  };
  // The first trip offered only to someone who has none (as Your trips has it).
  const firstTrip =
    trips.state !== "ready" ||
    (trips.data.trips.length === 0 &&
      trips.data.passes.length === 0 &&
      trips.data.deleted.length === 0 &&
      !me.tips_seen.includes("first_trip"));
  return <GettingStarted me={me} again={{ firstTrip, onClose: close }} onConnected={onChanged} onFinish={onChanged} />;
}

export function Profile({
  me,
  how,
  connect,
  onChanged,
}: {
  me: Me;
  how: boolean;
  connect: boolean;
  onChanged: () => void;
}) {
  // Connecting is for someone without an AI when the profile opened (it stays, through Connected, once one
  // connects), or since the last was disconnected here; with one, the profile connects another itself.
  const [noAi, setNoAi] = useState(() => me.ais.length === 0);
  // My AIs as a disconnection here left them, until I'm loaded again: what connecting starts from.
  const [left, setLeft] = useState<{ of: Me; ais: AiConnection[] } | null>(null);
  const onDisconnected = (ais: AiConnection[]) => {
    setLeft({ of: me, ais });
    if (ais.length === 0) setNoAi(true);
  };
  if (how) return <HowAgain />;
  if (connect && noAi)
    return <ConnectAgain me={left?.of === me ? { ...me, ais: left.ais } : me} onChanged={onChanged} />;
  return <YourProfile me={me} onChanged={onChanged} onDisconnected={onDisconnected} />;
}

function YourProfile({
  me,
  onChanged,
  onDisconnected,
}: {
  me: Me;
  onChanged: () => void;
  onDisconnected: (ais: AiConnection[]) => void;
}) {
  const [picked, setPicked] = useState<{ blob: Blob; url: string } | null>(null);
  const [name, setName] = useState(me.user.name);
  const [appearance, setAppearanceState] = useState<Appearance>(savedAppearance);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = "Your profile · Tripmo";
  }, []);
  useEffect(() => () => (picked ? URL.revokeObjectURL(picked.url) : undefined), [picked]);

  const act = async (run: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      await run();
      setSaved(done);
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const shown = picked?.url ?? me.user.photo ?? undefined;
  return (
    <div className="tm-page">
      <header className="tm-bar">
        <Wordmark />
        <span className="tm-grow" />
        <Button variant="quiet" size="sm" onClick={() => navigate("/")}>
          Your trips
        </Button>
      </header>
      <main className="tm-profile">
        <h1 className="display-l">Your profile</h1>

        <SettingsGroup id="me-title" title="Your photo, name and currency" unseen>
          <div className="tm-settings-part">
            <div className="tm-profile-photo">
              <Avatar
                name={me.user.name}
                size={72}
                className="tm-profile-avatar"
                {...(me.user.color ? { color: me.user.color } : {})}
                {...(shown ? { src: shown } : {})}
              />
              <div className="tm-profile-who">
                <p className="title tm-profile-me-name">{me.user.name}</p>
                <p className="caption tm-muted tm-profile-me-email">{me.user.email}</p>
                <div className="tm-profile-actions">
                  {picked ? (
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          act(async () => {
                            await api.setPhoto(picked.blob);
                            setPicked(null);
                          }, "Photo saved.")
                        }
                      >
                        Use this photo
                      </Button>
                      <Button
                        variant="quiet"
                        size="sm"
                        className="tm-inline-quiet"
                        disabled={busy}
                        onClick={() => setPicked(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="secondary" size="sm" disabled={busy} onClick={() => input.current?.click()}>
                        {me.user.photo ? "Change photo" : "Choose a photo"}
                      </Button>
                      {me.user.photo && (
                        <Button
                          variant="quiet"
                          size="sm"
                          className="tm-inline-quiet"
                          disabled={busy}
                          onClick={() => act(() => api.removePhoto(), "Photo removed; your initials show instead.")}
                        >
                          Remove photo
                        </Button>
                      )}
                    </>
                  )}
                  <input
                    ref={input}
                    type="file"
                    accept="image/*"
                    className="tm-hidden"
                    aria-label="Choose a photo"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      setError(null);
                      setSaved(null);
                      try {
                        const blob = await square(file);
                        setPicked({ blob, url: URL.createObjectURL(blob) });
                      } catch {
                        setError("That file isn't a photo this browser can open.");
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <p className="caption tm-muted">
              {picked
                ? "Cropped to a square from the middle. Use it, or choose another."
                : "Your photo and name are shown to the people in your trips, wherever you appear."}
            </p>
          </div>
          <form
            className="tm-settings-part tm-profile-name"
            onSubmit={(e) => {
              e.preventDefault();
              act(() => api.setName(name), "Name saved.");
            }}
          >
            <div className="tm-profile-field">
              <TextField
                label="Your name"
                value={name}
                onChange={(e) => setName((e.target as HTMLInputElement).value)}
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              type="submit"
              className="tm-profile-save"
              disabled={busy || name.trim() === me.user.name}
            >
              Save
            </Button>
          </form>
          <MyCurrency me={me} onChanged={onChanged} />
          {(error || saved) && (
            <div className="tm-settings-part">
              {error && (
                <p className="callout tm-error" role="alert">
                  {error}
                  <ReportThis />
                </p>
              )}
              {saved && (
                <p className="callout" role="status">
                  {saved}
                </p>
              )}
            </div>
          )}
        </SettingsGroup>

        <YourAis me={me} onDisconnected={onDisconnected} />

        <NotificationsSection />

        <SettingsGroup id="appearance-title" title="Appearance">
          <div className="tm-settings-part">
            <SegmentedControl
              label="Appearance"
              options={[
                { value: "system", label: "System" },
                { value: "light", label: "Light", icon: "sun" },
                { value: "dark", label: "Dark", icon: "moon" },
              ]}
              value={appearance}
              onChange={(v) => {
                setAppearanceState(v as Appearance);
                setAppearance(v as Appearance);
              }}
            />
            <p className="caption tm-muted">System follows this device's setting. Kept on this device.</p>
          </div>
        </SettingsGroup>

        <BillingSection />

        <SettingsGroup id="help-title" title="Help">
          <section className="tm-settings-part" aria-labelledby="how-title">
            <h3 id="how-title" className="tm-settings-label">
              How Tripmo works
            </h3>
            <p className="caption tm-muted">Planning with your AI, in four short screens.</p>
            <Button
              variant="secondary"
              size="sm"
              className="tm-edge-start"
              onClick={() => navigate("/profile?how=1", { state: { layer: true } })}
            >
              See how it works
            </Button>
            <p className="callout">Your AI knows the rest. Ask it:</p>
            <Prompt text={WHAT_CAN_I_DO} label="What to ask your AI" />
          </section>
          <section className="tm-settings-part" aria-labelledby="feedback-title">
            <h3 id="feedback-title" className="tm-settings-label">
              Feedback
            </h3>
            <p className="caption tm-muted">
              Something broken, or something you wish Tripmo did? Tell us: we read every one, and reply by email.
            </p>
            <SendFeedback />
          </section>
        </SettingsGroup>

        <SettingsGroup id="data-title" title="Your data and account">
          <YourData />
          <div className="tm-settings-part">
            <p className="callout">
              <span className="tm-muted">Signed in as </span>
              <span className="tm-profile-email">{me.user.email}</span>
            </p>
            <SignOuts />
          </div>
        </SettingsGroup>

        <SettingsGroup id="danger-title" title="Danger zone" danger>
          <DeleteAccount email={me.user.email} />
        </SettingsGroup>

        <Legal />
      </main>
    </div>
  );
}

// The AIs that have used Tripmo as me, by the name each gives itself (once per name, however many times
// it registered), with when each was last used, each with Disconnect (features/profile.md, "Disconnect an AI, sign
// out of other devices"): asked first, then its access ends and its row goes; and how to connect another, which
// shows up here as it connects. Tripmo can't see an AI forgetting it, so it says "last used", not "connected".
function YourAis({ me, onDisconnected }: { me: Me; onDisconnected: (ais: AiConnection[]) => void }) {
  const [adding, setAdding] = useState(false);
  const [heard, setHeard] = useState<AiConnection[] | null>(null);
  // The AI being disconnected (its row), and what was said once it was.
  const [asking, setAsking] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const said = useRef<HTMLParagraphElement>(null);
  usePersonEvents({ ais: setHeard }, adding);
  // Its row gone with the button that opened the dialog, what's said takes the focus.
  useEffect(() => {
    if (done) said.current?.focus();
  }, [done]);
  const rows = aiRows(heard ?? me.ais);

  const disconnect = async () => {
    if (!asking) return;
    setBusy(true);
    setError(null);
    try {
      const { ais } = await api.disconnectAi(asking.names);
      setHeard(ais);
      setDone(`${asking.name} is disconnected.`);
      setAsking(null);
      onDisconnected(ais);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsGroup id="ais-title" title="Your AIs">
      {rows.length > 0 && (
        <ul className="tm-settings-part tm-ais">
          {rows.map((row) => (
            <li key={row.name} className="tm-ai">
              <span className="tm-ai-text">
                <span className="callout tm-ai-name">{row.name}</span>
                <span className="caption tm-muted">
                  {row.last ? `Last used ${lastUsed(row.last)}` : "Not used yet"}
                </span>
              </span>
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Disconnect ${row.name}`}
                onClick={() => {
                  setDone(null);
                  setError(null);
                  setAsking(row);
                }}
              >
                Disconnect
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="tm-settings-part">
        {done && (
          <p ref={said} className="callout" role="status" tabIndex={-1}>
            {done}
          </p>
        )}
        {rows.length === 0 ? (
          <>
            <p className="caption tm-muted">None yet. Connect yours and it can plan your trips with you.</p>
            <Button
              variant="secondary"
              size="sm"
              className="tm-edge-start"
              onClick={() => navigate("/profile?connect=1", { state: { layer: true } })}
            >
              Connect your AI
            </Button>
          </>
        ) : (
          <p className="caption tm-muted">
            Each AI that has used Tripmo as you. Disconnecting one ends its access at once; connecting it again asks you
            again.
          </p>
        )}
        {rows.length === 0 ? null : adding ? (
          <ConnectSteps url={me.mcp_url} you={me.user.email} />
        ) : (
          <Button variant="secondary" size="sm" className="tm-edge-start" onClick={() => setAdding(true)}>
            Connect another AI
          </Button>
        )}
      </div>
      {asking && (
        <ConfirmDialog
          title={`Disconnect ${asking.name}?`}
          action="Disconnect"
          doing="Disconnecting…"
          busy={busy}
          error={error}
          onConfirm={disconnect}
          onCancel={() => {
            setAsking(null);
            setError(null);
          }}
        >
          <p className="body">It can't read or change your trips until you connect it again.</p>
        </ConfirmDialog>
      )}
    </SettingsGroup>
  );
}

// One row of Your AIs: the name shown, every name its registrations gave (none is "Your AI"), and when it was last
// used, null for allowed and not used yet.
interface Row {
  name: string;
  names: (string | null)[];
  last: string | null;
}

// My AIs by the name each gives itself, the last used first, then those not used yet.
function aiRows(ais: AiConnection[]): Row[] {
  const byName = new Map<string, Row>();
  for (const ai of ais) {
    const name = ai.name ?? "Your AI";
    const row = byName.get(name);
    if (!row) byName.set(name, { name, names: [ai.name], last: ai.last_at });
    else {
      if (!row.names.includes(ai.name)) row.names.push(ai.name);
      if (ai.last_at && (row.last === null || ai.last_at > row.last)) row.last = ai.last_at;
    }
  }
  return [...byName.values()].sort((a, b) => (b.last ?? "\uffff").localeCompare(a.last ?? "\uffff"));
}

// Sign out, and Sign out of other devices (features/profile.md): asked first, then every other phone, tablet and
// computer signed in as me is signed out and stops getting my notifications, saying how many; this one stays. Not in
// the Danger zone: signing in again there undoes it.
function SignOuts() {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const signOutOthers = async () => {
    setBusy(true);
    setError(null);
    try {
      setDone(signedOutLine(await api.signOutOthers()));
      setAsking(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="tm-sign-outs">
        <Button variant="quiet" size="sm" className="tm-inline-quiet" onClick={() => api.signOut()}>
          Sign out
        </Button>
        <Button
          variant="quiet"
          size="sm"
          className="tm-inline-quiet"
          onClick={() => {
            setDone(null);
            setError(null);
            setAsking(true);
          }}
        >
          Sign out of other devices
        </Button>
      </div>
      {done && (
        <p className="callout" role="status">
          {done}
        </p>
      )}
      {asking && (
        <ConfirmDialog
          title="Sign out of other devices?"
          action="Sign them out"
          doing="Signing out…"
          busy={busy}
          error={error}
          onConfirm={signOutOthers}
          onCancel={() => {
            setAsking(false);
            setError(null);
          }}
        >
          <p className="body">
            Every other phone, tablet and computer signed in as you is signed out, and stops getting your notifications.
            You stay signed in here.
          </p>
          <p className="body">
            Your AIs stay connected: disconnect one in Your AIs if it's on a device you no longer have.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

// "Signed out of 2 other devices."
function signedOutLine({ signed_out, devices }: SignedOutElsewhere): string {
  const count = (n: number) => `${n} other ${n === 1 ? "device" : "devices"}`;
  if (signed_out > 0) return `Signed out of ${count(signed_out)}.`;
  if (devices > 0) return `No other device was signed in. Notifications to ${count(devices)} stopped.`;
  return "No other device was signed in.";
}
